import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import constants from "../../constants/tutorialAutomaton";
import { useUser } from "../../context/userContext";
import { useGrammar } from "context/grammarContext";
import styles from "../../assets/jss/material-dashboard-react/components/dialogHistorical";
import "./classes.css";
import historicalDialogConstants from "../../constants/Dialogs/historicalDialogConstants";

const useStyles = makeStyles(styles);

/**
 * Componente React que exibe um diálogo histórico de gramáticas criadas pelo usuário.
 *
 * @component
 *
 * @returns {JSX.Element} O JSX para renderizar o diálogo histórico de gramáticas.
 *
 * @example
 * <HistoricalGrammarDialog />
 */

export default function HistoricalGrammarDialog() {
  const { language, user } = useUser();
  const {
    isActiveDialogHistoricalGrammar,
    setActiveDialogHistoricalGrammar,
    setOriginalGrammar,
    setGrammarName,
    setGrammarCreatedDate,
  } = useGrammar();

  const [historicalToday, setHistoricalToday] = useState([]);
  const [historicalYesterday, setHistoricalYesterday] = useState([]);
  const [historicalLastWeeks, setHistoricalLastWeeks] = useState([]);

  const classes = useStyles();

  /**
   * Ordena uma lista de gramáticas históricas com base na data de criação.
   */
  const sortHistorical = (historical, isAscending) => {
    return historical?.slice().sort((a, b) => {
      const dateA = new Date(a.lastUpdatedDate);
      const dateB = new Date(b.lastUpdatedDate);

      return isAscending ? dateA - dateB : dateB - dateA;
    });
  };

  /**
   * Atualiza os períodos históricos de gramáticas com base na data de criação.
   */
  const updateHistoricalPeriods = (sortedHistorical) => {
    const today = new Date();
    let historicalTempToday = [];
    let historicalTempYesterday = [];
    let historicalTempLastWeeks = [];

    sortedHistorical.forEach((hg) => {
      let grToHtml =
        typeof hg.variables === "string" && hg.variables.split("\n").length > 2
          ? `${hg.variables.split("\n")[0]}\n${
              hg.variables.split("\n")[1]
            }\n...`
          : hg.variables;

      const date = new Date(hg.lastUpdatedDate);

      const formattedDate = `${date.getDate()}/${
        date.getMonth() + 1
      }/${date.getFullYear()} - ${date.getHours()}:${String(
        date.getMinutes()
      ).padStart(2, "0")}`;

      const obj = {
        name: hg.name,
        view: grToHtml,
        original: hg.variables,
        date: formattedDate,
        createdDate: hg.createdDate, // Adicionando createdDate
      };

      if (
        today.getDate() === new Date(hg.lastUpdatedDate).getDate() &&
        today.getMonth() === new Date(hg.lastUpdatedDate).getMonth()
      ) {
        historicalTempToday.push(obj);
      } else if (
        today.getDate() - 1 === new Date(hg.lastUpdatedDate).getDate() &&
        today.getMonth() === new Date(hg.lastUpdatedDate).getMonth()
      ) {
        historicalTempYesterday.push(obj);
      } else {
        historicalTempLastWeeks.push(obj);
      }
    });

    setHistoricalToday(historicalTempToday);
    setHistoricalYesterday(historicalTempYesterday);
    setHistoricalLastWeeks(historicalTempLastWeeks);
  };

  /**
   * Fecha o diálogo histórico de gramáticas.
   */
  const handleClose = () => {
    setActiveDialogHistoricalGrammar(false);
  };

  /**
   * Seleciona uma gramática com base no período e no índice da lista.
   */
  const selectedGrammar = (list, index) => {
    if (list == "today") {
      setOriginalGrammar(historicalToday[index].original);
      setGrammarCreatedDate(historicalToday[index].createdDate);
      setGrammarName(historicalToday[index].name);
    } else if (list == "yesterday") {
      setOriginalGrammar(historicalYesterday[index].original);
      setGrammarCreatedDate(historicalYesterday[index].createdDate);
      setGrammarName(historicalYesterday[index].name);
    } else {
      setOriginalGrammar(historicalLastWeeks[index].original);
      setGrammarCreatedDate(historicalLastWeeks[index].createdDate);
      setGrammarName(historicalLastWeeks[index].name);
    }
    setActiveDialogHistoricalGrammar(false);
  };

  /**
   * Hook de efeito usado para atualizar os períodos históricos ao detectar mudanças no histórico de gramáticas.
   */
  useEffect(() => {
    if (
      Array.isArray(user.historicalGrammar) &&
      user.historicalGrammar.length > 0
    ) {
      const sortedHistorical = sortHistorical(user.historicalGrammar, false);
      updateHistoricalPeriods(sortedHistorical);
    } else {
      setHistoricalToday([]);
      setHistoricalYesterday([]);
      setHistoricalLastWeeks([]);
    }
  }, [user.historicalGrammar]);

  return (
    <div>
      <Dialog
        open={isActiveDialogHistoricalGrammar}
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle
          className={classes.dialog}
          id="alert-dialog-slide-title"
          style={{ backgroundColor: "rgb(243, 242, 242)" }}
        >
          <div className="wrapper-title">
            {constants.historic[language]}
            <div className="wrapper-button-close">
              <Button
                onClick={handleClose}
                style={{ backgroundColor: "#999", color: "#FFF" }}
              >
                X
              </Button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent
          className={`${classes.dialog} olhala`}
          style={{
            backgroundColor: "rgb(243, 242, 242)",
          }}
        >
          <Grid container className={classes.root} spacing={1}>
            {historicalToday != null && historicalToday.length > 0 && (
              <h3>{constants.today[language]}</h3>
            )}
            <Grid container spacing={1}>
              {historicalToday != undefined &&
                historicalToday.map((value, index) => (
                  <Grid item lg={4} xs={12} key={index}>
                    <div>
                      <span
                        style={{
                          fontSize: "0.85rem",
                          display: "block",
                          fontWeight: "bold",
                        }}
                      >
                        {value.name ||
                          historicalDialogConstants.machine.EmptyName[language]}
                      </span>
                      <span style={{ fontSize: ".75rem" }}>{value.date}</span>
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      <Button
                        key={index}
                        className="button-algorithm btn btn-secondary grString"
                        onClick={() => {
                          selectedGrammar("today", index);
                        }}
                      >
                        {value.view}
                      </Button>
                    </div>
                  </Grid>
                ))}
            </Grid>
          </Grid>
          <Grid container className={classes.root}>
            {historicalYesterday != null && historicalYesterday.length > 0 && (
              <h3>{constants.yesterday[language]}</h3>
            )}
            <Grid container spacing={1}>
              {historicalYesterday != undefined &&
                historicalYesterday.map((value, index) => (
                  <Grid item lg={4} xs={12} key={index}>
                    <div>
                      <span
                        style={{
                          fontSize: "0.85rem",
                          display: "block",
                          fontWeight: "bold",
                        }}
                      >
                        {value.name ||
                          historicalDialogConstants.machine.EmptyName[language]}
                      </span>
                      <span style={{ fontSize: ".75rem" }}>{value.date}</span>
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      <Button
                        className="button-algorithm btn btn-secondary grString"
                        onClick={() => {
                          selectedGrammar("yesterday", index);
                        }}
                      >
                        {value.view}
                      </Button>
                    </div>
                  </Grid>
                ))}
            </Grid>
          </Grid>
          <Grid container className={classes.root} spacing={1}>
            {historicalLastWeeks != null && historicalLastWeeks.length > 0 && (
              <h3>{constants.lastWeeks[language]}</h3>
            )}
            <Grid container spacing={1}>
              {historicalLastWeeks != undefined &&
                historicalLastWeeks.map((value, index) => (
                  <Grid item lg={4} xs={12} key={index}>
                    <div>
                      <span
                        style={{
                          fontSize: "0.85rem",
                          display: "block",
                          fontWeight: "bold",
                        }}
                      >
                        {value.name ||
                          historicalDialogConstants.machine.EmptyName[language]}
                      </span>
                      <span style={{ fontSize: ".75rem" }}>{value.date}</span>
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      <Button
                        key={index}
                        className=" button-algorithm btn btn-secondary grString"
                        onClick={() => {
                          selectedGrammar("lastWeeks", index);
                        }}
                      >
                        {value.view}
                      </Button>
                    </div>
                  </Grid>
                ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          style={{ backgroundColor: "rgb(243, 242, 242)" }}
          className={classes.dialog}
        ></DialogActions>
      </Dialog>
    </div>
  );
}
